//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { ForwardedRef, forwardRef } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import { TextInput } from '@components/atoms/text-input/TextInput';
import { FormFieldControllerProps } from '@components/form/field-controller/FormFieldController';

type FormTextInputProps<
  T extends FieldValues,
  TFieldName extends FieldPath<T>,
> = Omit<
  React.ComponentPropsWithoutRef<typeof TextInput>,
  'onChange' | 'errorText' | 'hasError' | 'name' | 'onBlur' | 'value'
> &
  FormFieldControllerProps<T, TFieldName> & { label?: React.ReactNode };

function FormTextInputInner<
  T extends FieldValues,
  TFieldName extends FieldPath<T>,
>(
  props: FormTextInputProps<T, TFieldName>,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const { error, invalid, label, onChange, ...rest } = props;
  return (
    <TextInput
      {...rest}
      ref={ref}
      errorText={error?.message}
      hasError={invalid}
      label={label}
      onChange={onChange}
    />
  );
}

/**
 * Generic forwardRef workaround for React 18 + TypeScript.
 *
 * TypeScript cannot infer generic type parameters through forwardRef's higher-order
 * function signature. The `as any` cast is only used internally for wiring - consumers
 * of this component still get full generic type inference at the call site.
 *
 * This is the standard community workaround documented in:
 * - https://github.com/microsoft/TypeScript/issues/30215
 *
 * React 19 will support `ref` as a regular prop, eliminating the need for forwardRef.
 * See: https://github.com/reactjs/rfcs/pull/107
 */
export const FormTextInput = forwardRef(FormTextInputInner as any) as {
  <T extends FieldValues, TFieldName extends FieldPath<T>>(
    props: FormTextInputProps<T, TFieldName> & {
      ref?: ForwardedRef<HTMLInputElement>;
    },
  ): React.ReactElement;
  displayName?: string;
};

FormTextInput.displayName = 'FormTextInput';
